import { Component, OnInit, inject } from '@angular/core';
import { ProfileAwardsService } from './profile-awards.service';
import { Award } from '../../shared/interfaces/award.interface';
import { WindowRefService } from '../../core/window-ref.service';
import { OpacityOutAnimation, StatusTagComponent } from '@exl-ng/mulo-common';
import { SEOService } from '../../core/seo.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { ProfileDataService } from '../profile-data.service';
import { ProfileService } from '../profile.service';
import { Profile } from '../../shared/configurations/profile.constant';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../parts/dialogs/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';
import { DefaultTranslatePipe } from '../../shared/pipes/default-translate.pipe';
import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonSplashComponent } from '../../bits/button-splash/button-splash.component';
import { CitationBriefComponent } from '../../parts/citations/citation-brief/citation-brief.component';
import { CitationBriefPlaceholderComponent } from '../../parts/citations/citation-brief-placeholder/citation-brief-placeholder.component';

@Component({
    selector: 'esp-profile-awards',
    templateUrl: './profile-awards.component.html',
    styleUrls: ['./profile-awards.component.scss'],
    animations: [OpacityOutAnimation],
    imports: [
        CitationBriefPlaceholderComponent,
        CitationBriefComponent,
        StatusTagComponent,
        ButtonSplashComponent,
        MatTooltip,
        MatIcon,
        SvgViewboxDirective,
        TranslateModule,
        DefaultTranslatePipe,
    ],
})
export class ProfileAwardsComponent implements OnInit {
    private profileAwardsService = inject(ProfileAwardsService);
    private windowRefService = inject(WindowRefService);
    profileDataService = inject(ProfileDataService);
    private SEOService = inject(SEOService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private translate = inject(TranslateService);
    profileService = inject(ProfileService);
    private matDialog = inject(MatDialog);

    awards: Award[] = [];
    isLoading = false;
    mainInstName: string;
    nameSubscription: Subscription;

    hideMenu = [{ name: 'research.profile.hide.action.hide' }];
    displayMenu = [{ name: 'research.profile.hide.action.display' }];
    emptyMenu = [];

    trackByIndex = (index: number, award: Award) => index;

    ngOnInit(): void {
        this.isLoading = true;
        this.profileAwardsService.getAwards().subscribe(
            (data) => {
                this.isLoading = false;
                this.awards = data as Award[];
                this.sortAwards();
                this.setHighlightsList();
            },
            () => {
                this.isLoading = false;
            },
        );
        this.seoSupport();
    }

    seoSupport() {
        this.mainInstName =
            this.configurationHandlerService.mainInstitutionName();
        //deep link to the direct tab - need to wait until the name updates
        this.nameSubscription = this.profileService.nameChange$.subscribe(
            (res) => {
                this.changeDocumentTitle(res);
            },
        );
        //moving between tabs
        if (this.profileDataService.displayName.value) {
            this.changeDocumentTitle(this.profileDataService.displayName.value);
        }
    }

    setHighlightsList() {
        this.profileAwardsService.highlights = this.awards
            .filter((award) => award.isHighlight)
            .map((award) => award.grantEntityId);
    }

    changeDocumentTitle(userName) {
        const code = this.getDocumentTitle();
        this.translate
            .get(code, {
                value0: userName,
                value1: this.mainInstName,
            })
            .subscribe((res) => {
                this.SEOService.updateTitle(res);
            });
    }

    getDocumentTitle() {
        let code = '';
        if (this.profileService.isPublicPage) {
            code = 'research.public.profile.awards.tab.document.title';
        } else {
            code = 'research.profile.awards.tab.document.title';
        }
        return code;
    }

    sortAwards() {
        this.awards.sort(function (a, b) {
            const bStart = b.grantStartYear ? b.grantStartYear : -1;
            const aStart = a.grantStartYear ? a.grantStartYear : -1;
            return bStart - aStart || a.grantName.localeCompare(b.grantName);
        });
    }

    goToUrl(url) {
        this.windowRefService.nativeWindow.open(url, '_blank');
    }

    getPostType(award: Award) {
        const years = [];
        if (award.grantStartYear) {
            years.push(award.grantStartYear);
        }
        if (award.grantEndYear) {
            years.push(award.grantEndYear);
        }
        if (years.length > 0) {
            return '| ' + years.join(' - ');
        }
        return undefined;
    }

    getMenuAction(award: Award) {
        if (this.profileService.isPublicPage) {
            return this.emptyMenu;
        }
        if (award.displayInPublicProfile) {
            return this.hideMenu;
        }
        return this.displayMenu;
    }

    getInvestigatorsStr(award: Award) {
        return award.investigators.join(', ');
    }

    onActionClick(action, award, index) {
        if (action === 'research.profile.hide.action.hide') {
            this.hideGrant(award);
        } else if (action === 'research.profile.hide.action.display') {
            this.displayGrant(award);
        }
    }

    hideGrant(award: Award) {
        this.profileAwardsService
            .hideGrantFromPublicProfile(award.grantEntityId)
            .subscribe(() => {
                award.displayInPublicProfile = false;
            });
    }

    displayGrant(award: Award) {
        this.profileAwardsService
            .displayGrantOnPublicProfile(award.grantEntityId)
            .subscribe(() => {
                award.displayInPublicProfile = true;
            });
    }

    showHighlightButton(award: Award) {
        return (
            !this.profileService.isPublicPage && award.displayInPublicProfile
        );
    }

    onHighlightClick(award: Award) {
        if (award.isHighlight) {
            this.removeHighlight(award);
        } else if (
            this.profileAwardsService.highlights.length <
            Profile.AWARDS_HIGHLIGHTS_LIMIT
        ) {
            this.addHighlight(award);
        } else if (
            this.profileAwardsService.highlights.length >=
            Profile.AWARDS_HIGHLIGHTS_LIMIT
        ) {
            this.openHighlightDialogLimitation();
        }
    }

    openHighlightDialogLimitation() {
        const dialogData = this.getHighlightDialogData();
        setTimeout(() => {
            this.matDialog
                .open(ConfirmDialogComponent, dialogData)
                .afterClosed()
                .subscribe((res) => {});
        }, 0);
    }

    getHighlightsTooltip(award: Award) {
        if (award.isHighlight) {
            return 'research.awards.list.highlight.remove.tooltip';
        }
        return 'research.awards.list.highlight.add.tooltip';
    }

    getHighlightDialogData() {
        return {
            data: {
                title: 'research.awards.list.highlight.dialog.limitation.title',
                titleParam: Profile.AWARDS_HIGHLIGHTS_LIMIT,
                message:
                    'research.awards.list.highlight.dialog.limitation.message',
                actions: {
                    confirm:
                        'research.awards.list.highlight.dialog.limitation.confirm',
                },
                actionFocus: 'confirm',
            },
        };
    }

    addHighlight(award: Award) {
        award.isHighlight = true;
        this.profileAwardsService.highlights.push(award.grantEntityId);
        this.profileAwardsService
            .addGrantHighlight(award.grantEntityId)
            .subscribe((data) => {});
    }

    removeHighlight(award: Award) {
        award.isHighlight = false;
        const index = this.profileAwardsService.highlights.indexOf(
            award.grantEntityId,
        );
        if (index !== -1) {
            this.profileAwardsService.highlights.splice(index, 1);
        }
        this.profileAwardsService
            .removeGrantHighlight(award.grantEntityId)
            .subscribe((data) => {});
    }

    getGrantTitle(award: Award) {
        if (award.grantName) {
            if (award.grantAcronym) {
                return award.grantName + ' - ' + award.grantAcronym;
            }
            return award.grantName;
        }
        return award.grantAcronym;
    }
}
